import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import Card from '../components/common/Card'
import PageContainer from '../components/layout/PageContainer'
import Button from '../components/common/Button'
import LoadingSpinner from '../components/common/LoadingSpinner'
import Pagination from '../components/common/Pagination'
import { useNotifications } from '../hooks/useNotifications'
import { formatTimeAgo } from '../utils/formatters'

const Notifications = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState(searchParams.get('status') || 'all') // all, unread, read

  const {
    list: notifications,
    listMeta,
    isListLoading,
    unreadCount,
    fetchList,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
  } = useNotifications()

  const page = parseInt(searchParams.get('page')) || 1

  useEffect(() => {
    fetchList({ status: filter, page, limit: 10 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page])

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextFilter === 'all') next.delete('status')
      else next.set('status', nextFilter)
      next.delete('page')
      return next
    })
  }

  const handlePageChange = (nextPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextPage === 1) next.delete('page')
      else next.set('page', String(nextPage))
      return next
    })
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return 'mdi:check-circle'
      case 'warning':
        return 'mdi:alert'
      case 'error':
        return 'mdi:alert-circle'
      case 'info':
        return 'mdi:information'
      default:
        return 'mdi:bell'
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success':
        return { text: 'text-success', bg: 'bg-success/20' }
      case 'warning':
        return { text: 'text-warning', bg: 'bg-warning/20' }
      case 'error':
        return { text: 'text-danger', bg: 'bg-danger/20' }
      case 'info':
        return { text: 'text-info', bg: 'bg-info/20' }
      default:
        return { text: 'text-primary', bg: 'bg-primary/20' }
    }
  }

  const markAsReadHandler = async (id) => {
    try {
      await markAsRead(id)
      toast.success('Notification marked as read')
    } catch {
      toast.error('Failed to mark notification as read')
    }
  }

  const markAsUnreadHandler = async (id) => {
    try {
      await markAsUnread(id)
      toast.info('Notification marked as unread')
    } catch {
      toast.error('Failed to mark notification as unread')
    }
  }

  const markAllAsReadHandler = async () => {
    try {
      await markAllAsRead()
      toast.success('All notifications marked as read')
    } catch {
      toast.error('Failed to mark all notifications as read')
    }
  }

  const deleteNotificationHandler = async (id) => {
    try {
      await deleteNotification(id)
      toast.success('Notification deleted')
    } catch {
      toast.error('Failed to delete notification')
    }
  }

  const deleteAllReadHandler = async () => {
    try {
      await deleteAllRead()
      toast.success('All read notifications deleted')
    } catch {
      toast.error('Failed to delete read notifications')
    }
  }

  const groupNotificationsByDate = (items) => {
    const groups = {}
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    items.forEach((notification) => {
      const notifDate = new Date(notification.createdAt)
      notifDate.setHours(0, 0, 0, 0)

      let groupKey
      if (notifDate.getTime() === today.getTime()) {
        groupKey = 'Today'
      } else if (notifDate.getTime() === yesterday.getTime()) {
        groupKey = 'Yesterday'
      } else {
        groupKey = new Date(notification.createdAt).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(notification)
    })

    return groups
  }

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications],
  )

  const hasReadOnPage = notifications.some((n) => n.read)

  return (
    <PageContainer title="Notifications" subtitle="View and manage all your notifications">
      {/* Filter and Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        {/* Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pt-1 pb-2 sm:pt-0 sm:pb-0">
          <button
            onClick={() => handleFilterChange('all')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === 'all'
                ? 'bg-primary text-white'
                : 'bg-bg-light text-text-secondary hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleFilterChange('unread')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors relative ${
              filter === 'unread'
                ? 'bg-primary text-white'
                : 'bg-bg-light text-text-secondary hover:bg-gray-200'
            }`}
          >
            Unread ({unreadCount})
            {unreadCount > 0 && (
              <span className="absolute  right-1 w-2 h-2 bg-danger rounded-full"></span>
            )}
          </button>
          <button
            onClick={() => handleFilterChange('read')}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === 'read'
                ? 'bg-primary text-white'
                : 'bg-bg-light text-text-secondary hover:bg-gray-200'
            }`}
          >
            Read
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {unreadCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              icon="mdi:check-all"
              onClick={markAllAsReadHandler}
              className="w-full sm:w-auto"
            >
              <span className="hidden sm:inline">Mark All Read</span>
              <span className="sm:hidden">Mark All</span>
            </Button>
          )}
          {hasReadOnPage && (
            <Button
              variant="secondary"
              size="sm"
              icon="mdi:delete-sweep"
              onClick={deleteAllReadHandler}
              className="w-full sm:w-auto"
            >
              <span className="hidden sm:inline">Delete Read</span>
              <span className="sm:hidden">Delete</span>
            </Button>
          )}
        </div>
      </div>

      {/* Loading */}
      {isListLoading && notifications.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : notifications.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedNotifications).map(([dateGroup, groupNotifications]) => (
            <Card key={dateGroup} className="mb-4">
              <h3 className="text-sm font-semibold text-text-secondary mb-3 sm:mb-4 uppercase tracking-wide">
                {dateGroup}
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {groupNotifications.map((notification) => {
                  const colors = getNotificationColor(notification.type)
                  return (
                    <div
                      key={notification.id}
                      className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all ${
                        !notification.read
                          ? 'bg-primary/5 border-primary/20'
                          : 'bg-bg-light border-border-light hover:border-primary/30'
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${colors.bg}`}
                      >
                        <Icon
                          icon={getNotificationIcon(notification.type)}
                          className={`text-lg sm:text-xl ${colors.text}`}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm sm:text-base font-semibold text-text-dark flex-1">
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-1.5"></div>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-text-secondary mb-2 line-clamp-2 sm:line-clamp-none">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <p className="text-xs text-text-secondary">
                            {formatTimeAgo(notification.createdAt)}
                          </p>
                          {notification.link && (
                            <Link
                              to={notification.link}
                              onClick={() => {
                                if (!notification.read) markAsReadHandler(notification.id)
                              }}
                              className="text-xs text-primary hover:underline font-medium flex items-center gap-1"
                            >
                              View Details
                              <Icon icon="mdi:arrow-right" className="text-sm" />
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-1 sm:gap-2 flex-shrink-0">
                        {!notification.read ? (
                          <button
                            onClick={() => markAsReadHandler(notification.id)}
                            className="p-2 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            aria-label="Mark as read"
                          >
                            <Icon icon="mdi:email-open" className="text-lg" />
                          </button>
                        ) : (
                          <button
                            onClick={() => markAsUnreadHandler(notification.id)}
                            className="p-2 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            aria-label="Mark as unread"
                          >
                            <Icon icon="mdi:email" className="text-lg" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotificationHandler(notification.id)}
                          className="p-2 text-text-secondary hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
                          aria-label="Delete notification"
                        >
                          <Icon icon="mdi:delete" className="text-lg" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
            <Icon icon="mdi:bell-off" className="text-5xl sm:text-6xl text-text-secondary mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-text-dark mb-2">
              No notifications
            </h3>
            <p className="text-sm sm:text-base text-text-secondary max-w-md">
              {filter === 'unread'
                ? "You're all caught up! No unread notifications."
                : filter === 'read'
                ? 'No read notifications to display.'
                : 'You have no notifications yet.'}
            </p>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {!isListLoading && notifications.length > 0 && (
        <Pagination
          currentPage={listMeta.page}
          totalPages={listMeta.totalPages}
          totalDocs={listMeta.totalResults}
          limit={listMeta.limit}
          hasNextPage={listMeta.page < listMeta.totalPages}
          hasPrevPage={listMeta.page > 1}
          onPageChange={handlePageChange}
        />
      )}
    </PageContainer>
  )
}

export default Notifications
